import { inject, Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject, Subscription, switchMap } from 'rxjs';
import { MicrosoftDialPlanGroup } from '../../shared/models/generated/ziroModels';
import { delayWhen, filter, map } from 'rxjs/operators';
import { GlobalPropertiesContext } from '../../shared/contexts/global-properties.context';
import { MicrosoftDialPlansFieldConfigContext } from '../../shared/contexts/microsoft-dial-plans-field-config.context';
import { AuditHeaderService } from '../../shared/services/audit-header.service';
import { Nvp } from '../../shared/models/nvp';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';

@Injectable()
export class MsDialPlanGroupsContext implements OnDestroy {
  private _http = inject(HttpClient);
  private auditHeaderService = inject(AuditHeaderService);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private microsoftDialPlansFieldConfigContext = inject(MicrosoftDialPlansFieldConfigContext);
  private _router = inject(Router);

  _stateSource = new ReplaySubject<MicrosoftDialPlanGroup[]>(1);
  state$ = this._stateSource.asObservable();

  private _currentTenantId: number | null = null;
  private _subscriptions = new Subscription();

  constructor() {
    this._checkCurrentRoute();
    const sub = this._router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        if (this._isOnTenantRoute(event.urlAfterRedirects)) {
          const match = event.urlAfterRedirects.match(/tenants\/(\d+)/);
          if (match) {
            const tenantId = Number(match[1]);
            if (tenantId && tenantId !== this._currentTenantId) {
              this.setTenantId(tenantId);
            }
          }
        }
      });

    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  getAll(tenantId: string | number): Observable<void> {
    return this._getAll(tenantId).pipe(
      map((groups) => {
        groups.forEach((group) => {
          group.dialPlanRangesJson.sort((a, b) => parseInt(a.start) - parseInt(b.start));
        });
        this._stateSource.next(groups);
      }),
    );
  }

  post(tenantId: string | number, group: MicrosoftDialPlanGroup): Observable<number> {
    return this._post(tenantId, group).pipe(
      delayWhen(() => forkJoin([this.getAll(tenantId), this.microsoftDialPlansFieldConfigContext.init(tenantId)])),
    );
  }

  put(tenantId: string | number, group: MicrosoftDialPlanGroup, auditTags?: Nvp[]): Observable<MicrosoftDialPlanGroup> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this._put(tenantId, group.id, group, httpOptions).pipe(
      delayWhen(() => forkJoin([this.getAll(tenantId), this.microsoftDialPlansFieldConfigContext.init(tenantId)])),
    );
  }

  delete(tenantId: string | number, groupId: string): Observable<void> {
    return this._delete(tenantId, groupId.toString()).pipe(
      delayWhen(() => forkJoin([this.getAll(tenantId), this.microsoftDialPlansFieldConfigContext.init(tenantId)])),
    );
  }

  save(tenantId: string | number, body: MicrosoftDialPlanGroup): Observable<void> {
    if (body.id == null) {
      return this.post(tenantId, body).pipe(map(() => {}));
    } else {
      return this.put(tenantId, body).pipe(map(() => {}));
    }
  }

  setTenantId(tenantId: number): void {
    this._currentTenantId = tenantId;
    const sub = this.globalPropertiesContext.state$
      .pipe(
        switchMap((globalProperties) => {
          const isWorkbench = window.location.href.includes('workbench');
          if (!globalProperties.hostedEnabled && !isWorkbench) {
            return of(null);
          }
          return this.getAll(tenantId);
        }),
      )
      .subscribe();
    this._subscriptions.add(sub);
  }

  private _buildHttpOptions(auditTags: Nvp[]): { headers?: HttpHeaders } {
    return auditTags ? { headers: this.auditHeaderService.buildHeader(auditTags) } : {};
  }

  private _checkCurrentRoute(): void {
    const currentUrl = this._router.url;
    this._extractTenantFromUrl(currentUrl);
  }

  private _extractTenantFromUrl(url: string): void {
    if (this._isOnTenantRoute(url)) {
      const match = url.match(/tenants\/(\d+)/);
      if (match) {
        const tenantId = Number(match[1]);
        if (tenantId && tenantId !== this._currentTenantId) {
          this.setTenantId(tenantId);
        }
      }
    }
  }

  private _isOnTenantRoute(url?: string): boolean {
    const urlToCheck = url || window.location.href.toLowerCase();
    return /\/tenants\/\d+/.test(urlToCheck);
  }

  private _getAll(tenantId: string | number): Observable<MicrosoftDialPlanGroup[]> {
    return this._http.get<MicrosoftDialPlanGroup[]>(`/services/microsoft/admin/tenants/${tenantId}/dial-plans`);
  }

  private _put(
    tenantId: string | number,
    id: string,
    body: MicrosoftDialPlanGroup,
    options: { headers?: HttpHeaders },
  ): Observable<MicrosoftDialPlanGroup> {
    return this._http.put<MicrosoftDialPlanGroup>(
      `/services/microsoft/admin/tenants/${tenantId}/dial-plans/${id}`,
      body,
      options,
    );
  }

  private _post(tenantId: string | number, body: MicrosoftDialPlanGroup): Observable<number> {
    return this._http.post<number>(`/services/microsoft/admin/tenants/${tenantId}/dial-plans`, body);
  }

  private _delete(tenantId: string | number, id: string): Observable<void> {
    return this._http.delete<void>(`/services/microsoft/admin/tenants/${tenantId}/dial-plans/${id}`);
  }
}
